/**
 * AllInclusiveGrid — 7 feature tiles per the Feb 2026 spec revision.
 *
 * Removed (vs. older copy):
 *   • "3D prints ship home" tile (per request)
 * Updated:
 *   • "Multiple locations" copy generalised across Aurora/Naperville + partners
 *   • Flexible scheduling tile rewritten — no per-session reference
 *   • Scholarships tile gets a "Contact us to learn more" link
 *
 * Layout: white tiles on a slate bg, each lifts on hover.
 */
import { Link } from 'react-router-dom';
import {
  Package, Users, Calendar, Clock, MapPin, Award, ArrowRight,
} from 'lucide-react';
import SectionHeading from './SectionHeading';
import CarouselGrid from './CarouselGrid';

const TILES = [
  {
    icon: Package, title: 'STEM kit included',
    body: 'Materials and take-home projects come with every camp. Nothing extra to buy.',
  },
  {
    icon: Users, title: 'Student-mentor led',
    body: 'Small groups guided by near-peer mentors who keep every day hands-on.',
  },
  {
    icon: Calendar, title: 'Flexible scheduling',
    body: "Choose a half-day or full-day week to fit your family's schedule. Options vary by location.",
  },
  {
    icon: Clock, title: 'Extended care',
    body: 'Early drop-off and late pick-up. $150/wk (both AM + PM) · $75/wk (one of AM or PM).',
  },
  {
    icon: MapPin, title: 'Multiple locations',
    body: 'Locations across Aurora and Naperville, plus select school partners and park districts.',
  },
  {
    icon: Award, title: 'Scholarships available',
    body: 'Need-based scholarship access through our partner, APS Training Academy.',
    cta: { label: 'Contact us to learn more', href: '/contact' },
  },
];

export default function AllInclusiveGrid() {
  return (
    <section
      id="included"
      className="bg-rsc-slate pt-16 sm:pt-20 pb-6 sm:pb-8 font-poppins scroll-mt-32"
      data-testid="all-inclusive-grid"
    >
      <div className="container-app">
        <SectionHeading
          eyebrow="All-inclusive"
          title="One price."
          accent="Everything in the box."
          body="No supply lists, no surprise add-ons. Every camper walks in and starts building on day one."
        />

        {/* Tiles — mobile: horizontal swipe rail (one-and-a-peek). md+:
            7-tile 2/3-col grid, with the orphan 7th tile centered in
            the middle column on lg via `lg:col-start-2`. */}
        <div className="mt-10 max-w-6xl mx-auto">
          <CarouselGrid variant="threeCol" data-testid="inc-grid">
            {TILES.map((t) => {
              const Icon = t.icon;
              return (
                <div
                  key={t.title}
                  className="h-full group rounded-2xl bg-white border border-rsc-card-border p-5 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
                  data-testid={`inc-tile-${t.title.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="w-11 h-11 rounded-xl bg-rsc-slate-soft border border-rsc-card-border flex items-center justify-center mb-3">
                    <Icon className="w-5 h-5 text-rsc-brand-blue" />
                  </div>
                  <h4 className="text-base font-extrabold text-rsc-brand-blue">{t.title}</h4>
                  <p className="mt-1.5 text-sm text-rsc-charcoal leading-relaxed">{t.body}</p>
                  {t.cta && (
                    <Link
                      to={t.cta.href}
                      className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-rsc-blue hover:text-rsc-brand-blue"
                    >
                      {t.cta.label}
                      <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  )}
                </div>
              );
            })}
          </CarouselGrid>
        </div>
      </div>
    </section>
  );
}
